/**
 * TypeScript types for billing system
 */

export enum PlanType {
  FREE = 'FREE',
  STARTER = 'STARTER',
  PROFESSIONAL = 'PROFESSIONAL',
  ENTERPRISE = 'ENTERPRISE'
}

export enum InvoiceStatus {
  PENDING = 'PENDING',
  PAID = 'PAID',
  OVERDUE = 'OVERDUE',
  CANCELLED = 'CANCELLED',
  FAILED = 'FAILED'
}

export enum SettlementStatus {
  PENDING = 'PENDING',
  PROCESSED = 'PROCESSED',
  RECONCILED = 'RECONCILED',
  DISPUTED = 'DISPUTED'
}

export enum PaymentProvider {
  SSLCOMMERZ = 'sslcommerz',
  STRIPE = 'stripe'
}

export enum Currency {
  BDT = 'BDT',
  USD = 'USD'
}

export interface CompanyBillingInfo {
  id: string;
  name: string;
  plan: PlanType;
  // Phase A4 marketing tier — single source of truth aligned with the
  // backend services.entitlements module (solo / sme / enterprise). Prefer
  // this field for gating + render. Backend derives it from company.tier
  // when set, else maps the legacy plan column. Optional only because old
  // mock fixtures may not include it; live backend always returns a value.
  tier?: string | null;
  quota_limit: number | null;
  quota_used: number;
  quota_remaining: number | null;
  billing_email: string | null;
  payment_customer_id: string | null;
}

export interface UsageStats {
  company_id: string;
  current_month: number;
  current_week: number;
  today: number;
  total_usage: number;
  total_cost: number;
  quota_limit: number | null;
  quota_used: number;
  quota_remaining: number | null;
}

export interface UsageRecord {
  id: string;
  company_id: string;
  user_id: string | null;
  session_id: string | null;
  action: string;
  cost: number;
  created_at: string;
  updated_at: string | null;
  metadata: Record<string, any> | null;
}

export interface UsageRecordList {
  records: UsageRecord[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

export interface InvoiceLineItem {
  id: string;
  description: string;
  quantity: number;
  unit_price: number;
  amount: number;
}

export interface Invoice {
  id: string;
  company_id: string;
  invoice_number: string;
  amount: number;
  currency: Currency;
  status: InvoiceStatus;
  issued_date: string;
  due_date: string;
  paid_date: string | null;
  payment_intent_id: string | null;
  payment_method: string | null;
  description: string | null;
  line_items: InvoiceLineItem[];
  created_at: string;
  updated_at: string | null;
}

export interface InvoiceList {
  invoices: Invoice[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

export interface PaymentIntent {
  id: string;
  amount: number;
  currency: Currency;
  status: string;
  client_secret: string | null;
  checkout_url: string | null;
  payment_method_types: string[];
  created_at: string;
  expires_at: string | null;
}

export interface PricingInfo {
  per_check: number;
  import_draft: number;
  import_bundle: number;
  currency: Currency;
}

export interface QuotaCheckResult {
  allowed: boolean;
  remaining: number | null;
  limit: number | null;
  message: string | null;
}

// Plan definitions
export interface PlanFeatures {
  name: string;
  quota: number | null;
  price: number;
  currency: Currency;
  features: string[];
  popular?: boolean;
}

// NOTE: names / quotas / features below reflect the 2026-05-10 pricing
// restructure (Trader track — Solo / Business / Enterprise; metering unit = one
// LC presentation). The `price` / `currency` fields are STALE BDT placeholders
// that predate the USD model and are only surfaced in the half-built
// UpgradeModal — canonical, localized pricing lives in `lib/pricing.ts`, and
// self-serve checkout (with real prices) is v1.1. `PlanType.FREE` is retained
// only as a legacy fallback; the new "free" mechanism is the public /check
// checker, not an in-app plan.
export const PLAN_DEFINITIONS: Record<PlanType, PlanFeatures> = {
  [PlanType.FREE]: {
    name: 'Free',
    quota: 0,
    price: 0,
    currency: Currency.BDT,
    features: ['Try a free LC check at /check', 'No in-app monthly quota — subscribe for a pool']
  },
  // Maps to backend tier `solo`.
  [PlanType.STARTER]: {
    name: 'Solo',
    quota: 5,
    price: 15000, // stale — see note above
    currency: Currency.BDT,
    features: ['5 LC presentations / month', '1 seat', 'Excel export', 'Validation history', 'Email support']
  },
  // Maps to backend tier `business` (and the agency tiers, via normalizePlanType).
  [PlanType.PROFESSIONAL]: {
    name: 'Business',
    quota: 25,
    price: 45000, // stale — see note above
    currency: Currency.BDT,
    features: ['25 LC presentations / month', '5 seats', 'API access', 'Custom branding', 'Analytics', 'Priority support'],
    popular: true
  },
  [PlanType.ENTERPRISE]: {
    name: 'Enterprise',
    quota: null, // 100 included + negotiated volume bands above ~150; `null` keeps isUnlimitedPlan() true for the meter
    price: 0, // custom pricing
    currency: Currency.BDT,
    features: ['100 LC presentations / month (volume bands above ~150)', '10 seats', 'Integrations', 'On-prem option', 'Custom rule sets', 'Dedicated account manager', 'SLA']
  }
};

// Admin types
export interface AdminCompanyStats {
  company_id: string;
  company_name: string;
  plan: PlanType;
  total_usage: number;
  total_cost: number;
  quota_limit: number | null;
  quota_used: number;
  last_activity: string | null;
  status: string;
}

export interface AdminUsageReport {
  period_start: string;
  period_end: string;
  total_companies: number;
  total_usage: number;
  total_revenue: number;
  companies: AdminCompanyStats[];
}

export interface SystemBillingKPIs {
  total_revenue_monthly: number;
  active_companies: number;
  delinquent_accounts: number;
  invoices_this_cycle: number;
  average_revenue_per_company: number;
  quota_utilization_average: number;
}

// API request types
export interface UsageRecordsFilters {
  page?: number;
  per_page?: number;
  start_date?: string;
  end_date?: string;
  action?: string;
  // Bank-specific filters
  client_id?: string;
  branch_id?: string;
  product?: string;
}

export interface InvoicesFilters {
  page?: number;
  per_page?: number;
  status?: InvoiceStatus;
  // Bank-specific filters
  client_id?: string;
  branch_id?: string;
  product?: string;
  settlement_status?: SettlementStatus;
}

export interface CheckoutRequest {
  plan: PlanType;
  provider: PaymentProvider;
  return_url?: string;
  cancel_url?: string;
  amount?: number;
  currency?: string;
  priceId?: string;
  quantity?: number;
  mode?: 'payment' | 'subscription';
  metadata?: Record<string, any>;
  paymentMethodTypes?: string[];
}

export interface CompanyBillingUpdate {
  plan?: PlanType;
  quota_limit?: number;
  billing_email?: string;
}

// Bank compliance types
export interface BankComplianceReport {
  period_start: string;
  period_end: string;
  total_companies: number;
  active_companies: number;
  total_usage: number;
  total_revenue: number;
  compliance_score: number;
  usage_trends: BankUsageTrend[];
  plan_distribution: PlanDistribution[];
  compliance_metrics: ComplianceMetric[];
}

export interface BankUsageTrend {
  month: string;
  total_usage: number;
  total_revenue: number;
  active_companies: number;
  compliance_score: number;
  avg_cost_per_company: number;
}

export interface PlanDistribution {
  plan: PlanType;
  companies: number;
  revenue: number;
  percentage: number;
}

export interface ComplianceMetric {
  metric: string;
  score: number;
  status: 'excellent' | 'good' | 'warning' | 'critical';
  trend: 'up' | 'down' | 'stable';
  description?: string;
}

export interface SMEMetrics {
  total_companies: number;
  active_companies: number;
  total_usage: number;
  total_revenue: number;
  compliance_score: number;
  avg_monthly_usage: number;
  top_plan: PlanType;
  usage_growth: number;
  revenue_growth: number;
  period: string;
}

export interface ComplianceFilters {
  period: '1m' | '3m' | '6m' | '12m';
  start_date?: string;
  end_date?: string;
  include_inactive?: boolean;
  plan_filter?: PlanType;
}

// Chart data types for analytics
export interface UsageTrendData {
  date: string;
  usage: number;
  cost: number;
}

export interface RevenueTrendData {
  date: string;
  revenue: number;
  plan_breakdown: Record<PlanType, number>;
}

export interface QuotaThresholds {
  warning: number; // percentage (e.g., 80)
  critical: number; // percentage (e.g., 95)
  exceeded: number; // percentage (100)
}

export const DEFAULT_QUOTA_THRESHOLDS: QuotaThresholds = {
  warning: 80,
  critical: 95,
  exceeded: 100
};

// Utility functions
export function formatCurrency(amount: number, currency: Currency = Currency.BDT): string {
  const symbols = {
    [Currency.BDT]: '৳',
    [Currency.USD]: '$'
  };

  return `${symbols[currency]}${amount.toLocaleString()}`;
}

export function getQuotaThreshold(used: number, limit: number | null): 'normal' | 'warning' | 'critical' | 'exceeded' {
  if (!limit) return 'normal';

  const percentage = (used / limit) * 100;

  if (percentage >= DEFAULT_QUOTA_THRESHOLDS.exceeded) return 'exceeded';
  if (percentage >= DEFAULT_QUOTA_THRESHOLDS.critical) return 'critical';
  if (percentage >= DEFAULT_QUOTA_THRESHOLDS.warning) return 'warning';
  return 'normal';
}

export function getPlanDisplayName(plan: PlanType): string {
  return PLAN_DEFINITIONS[plan].name;
}

export function isUnlimitedPlan(plan: PlanType): boolean {
  return PLAN_DEFINITIONS[plan].quota === null;
}

// Folds every billing-tier / plan string shape we might see into the
// frontend PlanType enum (FREE / STARTER / PROFESSIONAL / ENTERPRISE) so the
// billing UI keeps rendering. Inputs:
//   1. Frontend PlanType strings (passthrough).
//   2. Backend 2026-05-10 billing tiers: payg / solo / business / enterprise
//      / agency_starter / agency_pro / agency_enterprise.
//   3. Legacy backend strings: company-size "sme"; PlanType column values
//      free / pay_per_check / monthly_basic / monthly_pro / enterprise.
// Mapping: payg + solo → STARTER · business + agency_* + sme + monthly_pro
//   → PROFESSIONAL · enterprise + agency_enterprise → ENTERPRISE ·
//   free + pay_per_check + monthly_basic → STARTER.
// NOTE: the billing UI labels these "Starter/Professional/Enterprise" — a
// follow-up should replace PlanType in the billing surfaces with the raw
// tier string + a display-name map (see QuotaStrip.TIER_DISPLAY_NAMES) so
// "Business" shows as "Business", etc. Until then this keeps it functional.
export function normalizePlanType(plan: PlanType | string | null | undefined): PlanType | null {
  if (!plan) {
    return null;
  }

  const normalized = String(plan).toUpperCase();

  switch (normalized) {
    // Frontend PlanType passthrough
    case PlanType.FREE:
      return PlanType.FREE;
    case PlanType.STARTER:
      return PlanType.STARTER;
    case PlanType.PROFESSIONAL:
      return PlanType.PROFESSIONAL;
    case PlanType.ENTERPRISE:
      return PlanType.ENTERPRISE;
    // Backend 2026-05-10 billing tiers
    case "PAYG":
    case "SOLO":
      return PlanType.STARTER;
    case "BUSINESS":
    case "AGENCY_STARTER":
    case "AGENCY_PRO":
      return PlanType.PROFESSIONAL;
    case "AGENCY_ENTERPRISE":
      return PlanType.ENTERPRISE;
    // Legacy strings
    case "SME":
      return PlanType.PROFESSIONAL;
    case "PAY_PER_CHECK":
    case "MONTHLY_BASIC":
      return PlanType.STARTER;
    case "MONTHLY_PRO":
      return PlanType.PROFESSIONAL;
    default:
      return null;
  }
}

export function getInvoiceStatusColor(status: InvoiceStatus): string {
  switch (status) {
    case InvoiceStatus.PAID:
      return 'text-green-600 bg-green-100';
    case InvoiceStatus.PENDING:
      return 'text-yellow-600 bg-yellow-100';
    case InvoiceStatus.OVERDUE:
    case InvoiceStatus.FAILED:
      return 'text-red-600 bg-red-100';
    case InvoiceStatus.CANCELLED:
      return 'text-gray-600 bg-gray-100';
    default:
      return 'text-gray-600 bg-gray-100';
  }
}

// Bank-specific types
export interface BankContract {
  id: string;
  bank_id: string;
  contract_number: string;
  plan: PlanType;
  contract_term_months: number;
  start_date: string;
  end_date: string;
  quota_limit: number | null;
  overage_rate: number; // per validation
  billing_contact_name: string;
  billing_contact_email: string;
  billing_contact_phone: string | null;
  po_reference: string | null;
  next_settlement_date: string;
  payment_terms: string; // e.g., "Net-30", "Net-45"
  currency: Currency;
  status: 'active' | 'expired' | 'terminated';
  created_at: string;
  updated_at: string | null;
}

export interface Allocation {
  id: string;
  bank_id: string;
  client_id: string | null; // null for bank-wide allocation
  client_name: string | null;
  branch_id: string | null;
  branch_name: string | null;
  product: string | null; // e.g., "LC_VALIDATION", "RE_CHECK"
  budget_limit: number | null; // null for unlimited
  quota_limit: number | null;
  usage_current_period: number;
  usage_cost_current_period: number;
  remaining_budget: number | null;
  alerts_enabled: boolean;
  alert_threshold_percent: number; // e.g., 80 for 80% warning
  period_start: string;
  period_end: string;
  created_at: string;
  updated_at: string | null;
}

export interface AllocationList {
  allocations: Allocation[];
  total: number;
  page: number;
  per_page: number;
  pages: number;
}

export interface AllocationUpdate {
  budget_limit?: number | null;
  quota_limit?: number | null;
  alerts_enabled?: boolean;
  alert_threshold_percent?: number;
}
